/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { DeleteRCA } from '../models/DeleteRCA';
import type { GreenCardDocumentDetail } from '../models/GreenCardDocumentDetail';
import type { PaginatedGreenCardBordero } from '../models/PaginatedGreenCardBordero';
import type { PaginatedGreenCardBorderoList } from '../models/PaginatedGreenCardBorderoList';
import type { PaginatedGreenCardDocumentResponseList } from '../models/PaginatedGreenCardDocumentResponseList';
import type { PaginatedRcaBordero } from '../models/PaginatedRcaBordero';
import type { PaginatedRcaBorderoList } from '../models/PaginatedRcaBorderoList';
import type { PaginatedRcaBrokerAgentList } from '../models/PaginatedRcaBrokerAgentList';
import type { PaginatedRcaDocumentResponseList } from '../models/PaginatedRcaDocumentResponseList';
import type { PaginatedRcaEmployeeList } from '../models/PaginatedRcaEmployeeList';
import type { PaginatedRcaSyncJobListList } from '../models/PaginatedRcaSyncJobListList';
import type { PaginatedRcaSyncJobLotList } from '../models/PaginatedRcaSyncJobLotList';
import type { PayRca } from '../models/PayRca';
import type { RcaBrokerAgent } from '../models/RcaBrokerAgent';
import type { RCADocument } from '../models/RCADocument';
import type { RcaDocumentDetail } from '../models/RcaDocumentDetail';
import type { RcaEmployee } from '../models/RcaEmployee';
import type { RcaPolicyPreview } from '../models/RcaPolicyPreview';
import type { RcaSyncJobDetail } from '../models/RcaSyncJobDetail';
import type { RcaSyncJobEnqueue } from '../models/RcaSyncJobEnqueue';
import type { SearchRCA } from '../models/SearchRCA';
import type { SyncRcaDocuments } from '../models/SyncRcaDocuments';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class RcaService {

  /**
   * Common base for bordero Excel exports (keeps template styling and merges).
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - Website
 * * `mobile` - Mobile (iOS + Android)
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search 
   * @param startDate 
   * @returns PaginatedGreenCardBorderoList 
   * @throws ApiError
   */
  public static rcaGreencardBorderoList(
agentId?: number,
agentType?: 'broker' | 'employee' | 'mobile' | 'website',
endDate?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedGreenCardBorderoList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/greencard-bordero/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'end_date': endDate,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * List canceled Green Card contracts
   * Common base for bordero Excel exports (keeps template styling and merges).
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - Website
 * * `mobile` - Mobile (iOS + Android)
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search 
   * @param startDate 
   * @returns PaginatedGreenCardBordero 
   * @throws ApiError
   */
  public static rcaGreencardBorderoCanceledRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee' | 'mobile' | 'website',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedGreenCardBordero> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/greencard-bordero/canceled/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * List modified Green Card contracts
   * Common base for bordero Excel exports (keeps template styling and merges).
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - Website
 * * `mobile` - Mobile (iOS + Android)
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search 
   * @param startDate 
   * @returns PaginatedGreenCardBordero 
   * @throws ApiError
   */
  public static rcaGreencardBorderoModifiedRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee' | 'mobile' | 'website',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedGreenCardBordero> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/greencard-bordero/modified/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * Common base for bordero Excel exports (keeps template styling and merges).
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - Website
 * * `mobile` - Mobile (iOS + Android)
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search 
   * @param startDate 
   * @returns binary 
   * @throws ApiError
   */
  public static rcaGreencardBorderoXlsxRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee' | 'mobile' | 'website',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/greencard-bordero/xlsx/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * ViewSet for retrieving Green Card documents from database
   * @param contractNumber 
   * @param isCanceled 
   * @param isDeleted 
   * @param isValidated 
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param startDateFrom 
   * @param startDateTo 
   * @returns PaginatedGreenCardDocumentResponseList 
   * @throws ApiError
   */
  public static rcaGreencardDocumentsList(
contractNumber?: string,
isCanceled?: boolean,
isDeleted?: boolean,
isValidated?: boolean,
ordering?: string,
page?: number,
pageSize?: number,
startDateFrom?: string,
startDateTo?: string,
): CancelablePromise<PaginatedGreenCardDocumentResponseList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/greencard-documents/',
      query: {
        'contract_number': contractNumber,
        'is_canceled': isCanceled,
        'is_deleted': isDeleted,
        'is_validated': isValidated,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'start_date_from': startDateFrom,
        'start_date_to': startDateTo,
      },
    });
  }

  /**
   * ViewSet for retrieving Green Card documents from database
   * @param id 
   * @returns GreenCardDocumentDetail 
   * @throws ApiError
   */
  public static rcaGreencardDocumentsRetrieve(
id: number,
): CancelablePromise<GreenCardDocumentDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/greencard-documents/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param requestBody 
   * @returns binary 
   * @throws ApiError
   */
  public static rcaPolicyECecCreate(
requestBody: PayRca,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/rca/policy/e-cec/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Get a local RCA / Green Card document by policy ID or 404. without RCA Data sync.
   * @param policyId Policy ID with prefix (RCAI- for RCA, RCAE- for Green Card)
   * @returns RCADocument 
   * @throws ApiError
   */
  public static rcaPolicyGetRetrieve(
policyId: string,
): CancelablePromise<RCADocument> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/policy/get/',
      query: {
        'policy_id': policyId,
      },
    });
  }

  /**
   * Verify & Mark RCA/Green Card document as deleted in RCA Data by policy ID.
   * @param requestBody 
   * @returns RCADocument 
   * @throws ApiError
   */
  public static rcaPolicyMarkAsDeletedCreate(
requestBody: DeleteRCA,
): CancelablePromise<RCADocument> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/rca/policy/mark-as-deleted/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Preview RCA/Green Card policy
   * Get policy preview information using prefixed policy ID (RCAI-{id} or RCAE-{id})
   * @param requestBody 
   * @returns RcaPolicyPreview 
   * @throws ApiError
   */
  public static rcaPolicyPreviewCreate(
requestBody: SearchRCA,
): CancelablePromise<RcaPolicyPreview> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/rca/policy/preview/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Common base for bordero Excel exports (keeps template styling and merges).
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - Website
 * * `mobile` - Mobile (iOS + Android)
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search 
   * @param startDate 
   * @returns PaginatedRcaBorderoList 
   * @throws ApiError
   */
  public static rcaRcaBorderoList(
agentId?: number,
agentType?: 'broker' | 'employee' | 'mobile' | 'website',
endDate?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedRcaBorderoList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-bordero/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'end_date': endDate,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * List canceled RCA contracts
   * Common base for bordero Excel exports (keeps template styling and merges).
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - Website
 * * `mobile` - Mobile (iOS + Android)
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search 
   * @param startDate 
   * @returns PaginatedRcaBordero 
   * @throws ApiError
   */
  public static rcaRcaBorderoCanceledRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee' | 'mobile' | 'website',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedRcaBordero> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-bordero/canceled/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * List modified RCA contracts
   * Common base for bordero Excel exports (keeps template styling and merges).
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - Website
 * * `mobile` - Mobile (iOS + Android)
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search 
   * @param startDate 
   * @returns PaginatedRcaBordero 
   * @throws ApiError
   */
  public static rcaRcaBorderoModifiedRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee' | 'mobile' | 'website',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<PaginatedRcaBordero> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-bordero/modified/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * Common base for bordero Excel exports (keeps template styling and merges).
   * @param agentId 
   * @param agentType * `employee` - employee
 * * `broker` - broker
 * * `website` - Website
 * * `mobile` - Mobile (iOS + Android)
   * @param endDate 
   * @param ordering Which field to use when ordering the results.
   * @param search 
   * @param startDate 
   * @returns binary 
   * @throws ApiError
   */
  public static rcaRcaBorderoXlsxRetrieve(
agentId?: number,
agentType?: 'broker' | 'employee' | 'mobile' | 'website',
endDate?: string,
ordering?: string,
search?: string,
startDate?: string,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-bordero/xlsx/',
      query: {
        'agent_id': agentId,
        'agent_type': agentType,
        'end_date': endDate,
        'ordering': ordering,
        'search': search,
        'start_date': startDate,
      },
    });
  }

  /**
   * List broker agents with search by name or IDNO
   * @param ordering Ordering fields: name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, company_idno
   * @returns PaginatedRcaBrokerAgentList 
   * @throws ApiError
   */
  public static rcaRcaBrokersList(
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedRcaBrokerAgentList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-brokers/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * List broker agents with search by name or IDNO
   * @param id 
   * @returns RcaBrokerAgent 
   * @throws ApiError
   */
  public static rcaRcaBrokersRetrieve(
id: number,
): CancelablePromise<RcaBrokerAgent> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-brokers/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * ViewSet for retrieving RCA documents from database
   * @param contractNumber 
   * @param isCanceled 
   * @param isDeleted 
   * @param isValidated 
   * @param ordering Which field to use when ordering the results.
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param startDateFrom 
   * @param startDateTo 
   * @returns PaginatedRcaDocumentResponseList 
   * @throws ApiError
   */
  public static rcaRcaDocumentsList(
contractNumber?: string,
isCanceled?: boolean,
isDeleted?: boolean,
isValidated?: boolean,
ordering?: string,
page?: number,
pageSize?: number,
startDateFrom?: string,
startDateTo?: string,
): CancelablePromise<PaginatedRcaDocumentResponseList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-documents/',
      query: {
        'contract_number': contractNumber,
        'is_canceled': isCanceled,
        'is_deleted': isDeleted,
        'is_validated': isValidated,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'start_date_from': startDateFrom,
        'start_date_to': startDateTo,
      },
    });
  }

  /**
   * ViewSet for retrieving RCA documents from database
   * @param id 
   * @returns RcaDocumentDetail 
   * @throws ApiError
   */
  public static rcaRcaDocumentsRetrieve(
id: number,
): CancelablePromise<RcaDocumentDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-documents/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * List employees with search by name or IDNP
   * @param ordering Ordering fields: full_name
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: full_name, idnp
   * @returns PaginatedRcaEmployeeList 
   * @throws ApiError
   */
  public static rcaRcaEmployeesList(
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedRcaEmployeeList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-employees/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * List employees with search by name or IDNP
   * @param id 
   * @returns RcaEmployee 
   * @throws ApiError
   */
  public static rcaRcaEmployeesRetrieve(
id: number,
): CancelablePromise<RcaEmployee> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/rca-employees/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Enqueue RCA sync job
   * @param requestBody 
   * @returns RcaSyncJobEnqueue Job created successfully.
   * @throws ApiError
   */
  public static rcaSyncCreate(
requestBody: SyncRcaDocuments,
): CancelablePromise<RcaSyncJobEnqueue> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/rca/sync/',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Period already synced or covered by an active job.`,
      },
    });
  }

  /**
   * @param ordering Ordering fields: created, start_date, completed_at, started_at
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param source * `manual` - Manual
 * * `scheduled` - Scheduled
   * @param status * `queued` - Queued
 * * `running` - Running
 * * `completed` - Completed
 * * `completed_with_errors` - Completed With Errors
 * * `failed` - Failed
   * @returns PaginatedRcaSyncJobListList 
   * @throws ApiError
   */
  public static rcaSyncJobsList(
ordering?: string,
page?: number,
pageSize?: number,
source?: 'manual' | 'scheduled',
status?: 'completed' | 'completed_with_errors' | 'failed' | 'queued' | 'running',
): CancelablePromise<PaginatedRcaSyncJobListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/sync-jobs/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'source': source,
        'status': status,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this rca sync job.
   * @returns RcaSyncJobDetail 
   * @throws ApiError
   */
  public static rcaSyncJobsRetrieve(
id: number,
): CancelablePromise<RcaSyncJobDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/sync-jobs/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * @param id A unique integer value identifying this rca sync job.
   * @param ordering Ordering fields: created, start_date, completed_at, started_at
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param source * `manual` - Manual
 * * `scheduled` - Scheduled
   * @param status * `queued` - Queued
 * * `running` - Running
 * * `completed` - Completed
 * * `completed_with_errors` - Completed With Errors
 * * `failed` - Failed
   * @returns PaginatedRcaSyncJobLotList 
   * @throws ApiError
   */
  public static rcaSyncJobsLotsList(
id: number,
ordering?: string,
page?: number,
pageSize?: number,
source?: 'manual' | 'scheduled',
status?: 'completed' | 'completed_with_errors' | 'failed' | 'queued' | 'running',
): CancelablePromise<PaginatedRcaSyncJobLotList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/sync-jobs/{id}/lots/',
      path: {
        'id': id,
      },
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'source': source,
        'status': status,
      },
    });
  }

  /**
   * Retrieve the currently running RCA sync job, if any.
   * @returns RcaSyncJobDetail 
   * @throws ApiError
   */
  public static rcaSyncJobsActiveRetrieve(): CancelablePromise<RcaSyncJobDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/rca/sync-jobs/active/',
    });
  }

}
